import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Clock, Check, X } from "lucide-react";
import { listarTempoAjustesPendentes, aprovarTempoAjuste, rejeitarTempoAjuste } from "../services/tarefas";
import { useAuth } from "../hooks/useAuth";

function fmtHoras(ms: number) {
  const min = Math.round(Math.abs(ms) / 60000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${m > 0 ? String(m).padStart(2, "0") : ""}` : `${m}min`;
}

/**
 * Fila central de aprovação de ajustes de tempo (gestor/admin). Correções feitas
 * pela equipe entram como "pendente" e só contam nos relatórios depois de aprovadas.
 * Também dá pra aprovar/rejeitar direto no card da tarefa e via notificação.
 */
export function AprovacoesTempo() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const podeVer = can(["gestor", "admin"]);

  const { data: pendentes = [], isLoading } = useQuery({
    queryKey: ["tempo-ajustes-pendentes"],
    queryFn: listarTempoAjustesPendentes,
    enabled: podeVer,
  });

  function invalidar() {
    qc.invalidateQueries({ queryKey: ["tempo-ajustes-pendentes"] });
    qc.invalidateQueries({ queryKey: ["tempo-ajustes"] });
    qc.invalidateQueries({ queryKey: ["tarefas"] });
    qc.invalidateQueries({ queryKey: ["tarefa"] });
  }
  const aprovarM = useMutation({
    mutationFn: (id: string) => aprovarTempoAjuste(id),
    onSuccess: invalidar,
    onError: (e: any) => alert(e?.response?.data?.error?.message || "Não foi possível aprovar."),
  });
  const rejeitarM = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo?: string }) => rejeitarTempoAjuste(id, motivo),
    onSuccess: invalidar,
    onError: (e: any) => alert(e?.response?.data?.error?.message || "Não foi possível rejeitar."),
  });
  const ocupado = aprovarM.isPending || rejeitarM.isPending;

  if (!podeVer) return <div className="text-slate-500 text-sm">Acesso restrito à gestão.</div>;

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <Clock size={22} className="text-blue-600 dark:text-blue-400" /> Ajustes de tempo pendentes
          {pendentes.length > 0 && (
            <span className="text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5">{pendentes.length}</span>
          )}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
          Correções de tempo feitas pela equipe aguardando sua aprovação. Só contam nos relatórios depois de aprovadas.
        </p>
      </div>

      {isLoading && <div className="text-sm text-slate-400">Carregando…</div>}

      {!isLoading && pendentes.length === 0 && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-10 text-center">
          <Check size={26} className="mx-auto text-emerald-400 mb-2" />
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Nenhum ajuste pendente.</p>
        </div>
      )}

      <ul className="space-y-2">
        {pendentes.map((c) => (
          <li key={c.id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-3 flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-700 dark:text-slate-200">
                <span className="font-medium">{c.autor?.nome || "—"}</span> quer corrigir o tempo de{" "}
                <span className="font-medium">{c.usuario?.nome || "—"}</span>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {c.tarefa ? (
                  <Link to={`/tarefas?tarefa=${c.tarefa.id}`} className="text-blue-600 hover:underline">{c.tarefa.titulo}</Link>
                ) : (
                  "—"
                )}
                {c.tarefa?.projeto && <> · {c.tarefa.projeto.nome}</>}
              </div>
              <div className="text-xs mt-0.5 text-slate-600 dark:text-slate-300">
                {fmtHoras(c.totalAnteriorMs)} → <b>{fmtHoras(c.totalNovoMs)}</b>{" "}
                <span className="text-slate-400">({c.deltaMs >= 0 ? "+" : "−"}{fmtHoras(c.deltaMs)})</span>
                {" · "}<span className="italic text-slate-500">{c.motivo}</span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">{new Date(c.criadoEm).toLocaleString("pt-BR")}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => aprovarM.mutate(c.id)}
                disabled={ocupado}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
              >
                <Check size={15} /> Aprovar
              </button>
              <button
                onClick={() => { const m = window.prompt("Motivo da rejeição (opcional):") ?? undefined; rejeitarM.mutate({ id: c.id, motivo: m }); }}
                disabled={ocupado}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              >
                <X size={15} /> Rejeitar
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
